import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiUser, FiMail, FiLock, FiPhone, FiShoppingCart, FiPackage, FiMapPin } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderAPI, cartAPI, authAPI } from '../services/api';
import { sendWhatsAppMessage, createOrderMessage } from '../utils/whatsapp';

const CheckoutModal = ({ isOpen, onClose }) => {
  const { cartItems: clearCart, closeCart } = useCart();
  const { user, isAuthenticated, login } = useAuth();
  
  const [dbCartItems, setDbCartItems] = useState([]);
  const [dbCartTotal, setDbCartTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    customerName: '',
    city: '',
    address: '',
    phone: '',
    email: '',
  });

  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryBgHover = 'hover:bg-yellow-500';
  const primaryLight = 'bg-yellow-50';
  const primaryBorder = 'border-yellow-400';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-400';
  const secondaryBgHover = 'hover:bg-gray-500';
  const secondaryLight = 'bg-gray-100';
  const secondaryBorder = 'border-gray-400';

  // Charger les données selon l'authentification
  useEffect(() => {
    if (isOpen) {
      fetchCartFromDB();
      setFormData(prev => ({
        ...prev,
        customerName: user?.name || '',
        phone: user?.phone || '',
        email: user?.email || '',
      }));
    }
  }, [isOpen, isAuthenticated, user]);

  // Récupérer le panier depuis la BD
  const fetchCartFromDB = async () => {
    try {
      if(isAuthenticated) {
        const response = await cartAPI.getCart();
        
        if (response.success) { 
          setDbCartItems(response.data.items || []);
          const total = response.data.total_price || 0;
          setDbCartTotal(total);
        }
        
      }else{
        const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        setDbCartItems(guestCart);
        setDbCartTotal(guestCart.reduce((total, item) => total + (item.price || 0) * (item.quantity || 1), 0));
      
      }
    } catch (error) {
      console.error('Erreur récupération panier:', error);
      setDbCartItems([]);
      setDbCartTotal(0);
    }
  };

  const currentCartItems = dbCartItems;
  const currentTotal = dbCartTotal;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const generatePassword = (name, phone) => {
    if (!name || !phone) return '';
    const namePart = name.substring(0, 2).toLowerCase();
    const phoneDigits = phone.replace(/\D/g, '');
    const phonePart = phoneDigits.substring(0, 4);
    const nameLast = name.substring(name.length - 1).toLowerCase();
    return `${namePart}${phonePart}${nameLast}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let userId = null;
      let userEmail = '';
      let username = '';
      let currentUser = null;

      if (!isAuthenticated) {
        const password = generatePassword(formData.customerName, formData.phone);
        
        try {
          const userResponse = await authAPI.register({
            name: formData.customerName,
            email: formData.email || 'adresse_mail@nonrempli.com',
            phone: formData.phone,
            password: password,
            password_confirmation: password,
            role_id: 3
          });

          if (userResponse.success) {
            userId = userResponse.user.id;
            userEmail = formData.email;
            username = userResponse.user.name;
            
            try {
              const loginResponse = await authAPI.login({
                login: username,
                password: password
              });

              if (loginResponse.access_token) {
                localStorage.setItem('auth_token', loginResponse.access_token);
                if (loginResponse.user) {
                  login(loginResponse.user);
                }
              }
            } catch (loginError) {
              console.warn('Connexion automatique échouée:', loginError);
            }
          }
        } catch (error) {
          console.warn('Création de compte échouée:', error);
        }
      } else if (isAuthenticated) {
        userId = user?.id || null;
        userEmail = user?.email || '';
        currentUser = user;
      
        try {
          const token = localStorage.getItem('auth_token');
          if (token) {
            login(user);
          }
        } catch (error) {
          console.warn('Erreur rafraîchissement token:', error);
        }
      }

      const orderData = {
        user_id: userId,
        recipient_name: formData.customerName,
        recipient_email: userEmail || formData.email,
        recipient_country: formData.country,
        recipient_city: formData.city,
        recipient_address: formData.address,
        recipient_phone: formData.phone,
        status: 'pending',
        products: currentCartItems.map(item => ({
          product_id: item.product_id || item.id,
          quantity: item.quantity,
          unit_price: item.unit_price || item.price,
        })),
        total_amount: currentTotal,
        is_guest_order: !isAuthenticated && !userId
      };

      const orderResponse = await orderAPI.create(orderData);

      if (orderResponse.data?.success || orderResponse.data?.data?.success) {
        const whatsappMessage = createOrderMessage(orderData, currentCartItems);
        await sendWhatsAppMessage('680219057', whatsappMessage);
        console.log()

        localStorage.removeItem('guest_cart');

        if (isAuthenticated) {
          await clearCartFromDB(userId);
        }

        const orderId = orderResponse.data?.id || orderResponse.data.data?.id;
        alert(`✅ Commande confirmée !`);
        onClose();
        closeCart();
      }
      
    } catch (error) {
      console.error('Erreur:', error.response?.data || error.message);
      alert(`❌ Erreur: ${error.response?.data?.message || 'Veuillez réessayer.'}`);
    } finally {
      setLoading(false);
    }
  };

  // Vider le panier BD
  const clearCartFromDB = async (userId) => {
    try {
      await cartAPI.clearCart(userId);
      setDbCartItems([]);
      setDbCartTotal(0);
    } catch (error) {
      console.error('Erreur vidage panier BD:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Header avec nouvelle charte */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${primaryLight} rounded-lg`}>
              <FiPackage className={`text-xl ${primaryColor}`} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Finalisation de commande</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isAuthenticated ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 border border-yellow-200' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-200'}`}>
                  {isAuthenticated ? '🟢 Connecté' : '🔵 Invité'}
                </span>
                {isAuthenticated && user && (
                  <span className="text-sm text-gray-600">• {user.name}</span>
                )}
              </div>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className={`p-2 ${secondaryLight} hover:bg-gray-200 rounded-xl transition-all duration-200`}
          >
            <FiX className="text-2xl text-gray-600 hover:text-gray-900" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section compte invité avec nouvelle charte */}
          {!isAuthenticated && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-300">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 ${primaryLight} rounded-lg`}>
                  <FiUser className={`text-lg ${primaryColor}`} />
                </div>
                <h3 className="font-bold text-gray-900 text-lg">Création de compte (optionnelle)</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FiMail className="inline mr-2 text-gray-500" /> Adresse email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white"
                    placeholder="exemple@email.com"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Un compte vous sera créé pour suivre vos commandes (Mot de passe = 2 premières lettres du nom + 4 premiers chiffres du téléphone + dernière lettre du nom)
                  </p>
                </div>
                
                {formData.customerName && formData.phone && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-1.5 ${primaryLight} rounded`}>
                        <FiLock className={`text-sm ${primaryColor}`} />
                      </div>
                      <span className="text-sm font-semibold text-gray-700">Identifiants générés</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1 font-medium">Mot de passe</label>
                        <div className="text-sm bg-gray-50 p-3 rounded-lg border border-gray-200 font-mono text-gray-800">
                          {generatePassword(formData.customerName, formData.phone)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1 font-medium">Identifiant</label>
                        <div className="text-sm bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800">
                          Votre email
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informations de livraison avec nouvelle charte */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className={`p-2 ${primaryLight} rounded-lg`}>
                <FiMapPin className={`text-lg ${primaryColor}`} />
              </div>
              <h3 className="font-bold text-gray-900 text-lg">Informations de livraison</h3>
            </div>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nom complet *</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white"
                  placeholder="Ex: Jean Dupont"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ville *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white"
                    placeholder="Ex: Douala"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pays *</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white"
                    placeholder="Ex: Cameroun"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse complète *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white resize-none"
                  placeholder="Ex: Immeuble X, Rue Y, près de Z"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 ${primaryLight} rounded`}>
                      <FiPhone className={`text-sm ${primaryColor}`} />
                    </div>
                    <span>Numéro de téléphone *</span>
                  </div>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white"
                  placeholder="Ex: 6XX XX XX XX"
                />
              </div>
            </div>
          </div>

          {/* Récapitulatif avec nouvelle charte */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-300">
            <div className="flex items-center gap-3 mb-5">
              <div className={`p-2 ${primaryLight} rounded-lg`}>
                <FiShoppingCart className={`text-lg ${primaryColor}`} />
              </div>
              <h3 className="font-bold text-gray-900 text-lg">Récapitulatif</h3>
            </div>
            
            <div className="space-y-4">
              {currentCartItems.length === 0 ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiShoppingCart className="text-2xl text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">Votre panier est vide</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                    {currentCartItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <span className="font-semibold text-gray-900">{item.product?.name || item.name}</span>
                          <span className="text-gray-600 ml-2 text-sm">× {item.quantity}</span>
                        </div>
                        <span className="font-bold text-gray-900">
                          {(item.quantity * (item.unit_price || item.price)).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-300 pt-4">
                    <div className="flex justify-between items-center text-lg">
                      <span className="text-gray-900 font-bold">Total à payer</span>
                      <span className={`font-bold ${primaryColor}`}>
                        {currentTotal.toLocaleString('fr-FR')} FCFA
                      </span>
                    </div>
                    
                    {!isAuthenticated && (
                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-800 flex items-start gap-2">
                          <span className="font-bold">ℹ️ Note :</span> 
                          <span>
                            {formData.email ? 
                              'Un compte sera créé pour suivre vos commandes.' : 
                              'Sans email, vous ne pourrez pas suivre cette commande.'}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Boutons d'action avec nouvelle charte */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3.5 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || currentCartItems.length === 0}
              className={`flex-1 px-6 py-3.5 ${primaryBg} text-white rounded-xl font-bold ${primaryBgHover} shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Traitement...
                </>
              ) : (
                <>
                  <FiCheck className="text-lg" />
                  {isAuthenticated ? 'Confirmer la commande' : 'Passer la commande'}
                </>
              )}
            </button>
          </div>
          
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              En confirmant, vous acceptez nos conditions générales de vente.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutModal;